import { useState, type FormEvent } from 'react';
import { api, ApiError } from '@/lib/api.ts';

interface Props {
  /** False before any password exists — first run shows setup copy instead. */
  configured: boolean;
  onAuthenticated: () => void;
}

export function Login({ configured, onAuthenticated }: Props) {
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const isSetup = !configured;

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);

    if (isSetup && password !== confirm) {
      setError('Passwords do not match.');
      return;
    }

    setBusy(true);
    try {
      if (isSetup) {
        await api.auth.setup(password);
      } else {
        await api.auth.login(password);
      }
      onAuthenticated();
    } catch (err) {
      setError(messageFor(err));
      setPassword('');
      setConfirm('');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="flex min-h-full items-center justify-center px-6 py-16">
      <form onSubmit={handleSubmit} className="w-full max-w-xs">
        <h1 className="text-2xl font-semibold tracking-tight">TDL</h1>
        <p className="mt-1 mb-8 text-sm" style={{ color: 'var(--color-ink-2)' }}>
          {isSetup ? 'Choose a password to secure this app.' : 'Enter your password to continue.'}
        </p>

        <label className="block">
          <span className="sr-only">Password</span>
          <input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            autoFocus
            autoComplete={isSetup ? 'new-password' : 'current-password'}
            placeholder="Password"
            className="w-full rounded-md px-3 py-2 outline-none focus:ring-2"
            style={{
              background: 'var(--color-inset)',
              border: '1px solid var(--color-line)',
              color: 'var(--color-ink)',
            }}
          />
        </label>

        {isSetup && (
          <label className="mt-3 block">
            <span className="sr-only">Confirm password</span>
            <input
              type="password"
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              autoComplete="new-password"
              placeholder="Confirm password"
              className="w-full rounded-md px-3 py-2 outline-none focus:ring-2"
              style={{
                background: 'var(--color-inset)',
                border: '1px solid var(--color-line)',
                color: 'var(--color-ink)',
              }}
            />
          </label>
        )}

        {isSetup && (
          <p className="mt-3 text-xs leading-relaxed" style={{ color: 'var(--color-ink-2)' }}>
            This app will be reachable from the internet, so use a long, unique password —
            at least 12 characters.
          </p>
        )}

        {error && (
          <p role="alert" className="mt-3 text-sm" style={{ color: 'var(--color-brick)' }}>
            {error}
          </p>
        )}

        <button
          type="submit"
          disabled={busy || password.length === 0}
          className="mt-5 w-full rounded-md px-3 py-2 text-sm font-medium text-white transition disabled:opacity-50"
          style={{ background: 'var(--color-brass)' }}
        >
          {busy ? 'Working…' : isSetup ? 'Set password' : 'Log in'}
        </button>
      </form>
    </div>
  );
}

function messageFor(err: unknown): string {
  if (!(err instanceof ApiError)) return 'Something went wrong. Try again.';

  switch (err.code) {
    case 'invalid_credentials':
      return 'Incorrect password.';
    case 'too_many_attempts':
      return 'Too many attempts. Wait 15 minutes and try again.';
    case 'setup_requires_local_access':
      return 'The first password must be set on the computer running TDL.';
    case 'invalid_password':
      return err.message || 'Password must be at least 12 characters.';
    default:
      return err.message || 'Login failed.';
  }
}
